import {Box, Button, Card, CardContent, Stack, Typography, useTheme} from "@mui/material";
import * as React from "react";
import {useEffect, useState} from "react";
import {ApiRequestFailed, authenticateAndFetchData, retryFn} from "@lib/apis";
import {useQuery} from "@tanstack/react-query";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {styled} from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import { format } from "date-fns";

interface PageData {
	status_code: number
	status_message: string

	campaign_name: string
	emails_per_day: number
	total_contacts: number
	total_schedules: number
	total_emails_sent: number
	total_bounced: number
	total_unsubscribes: number
	total_replies: number
	campaign_status: string
	campaign_activities: CampaignActivity[]
	domains: Array<string>
}

interface CampaignActivity {
	id: number
	event_type: string
	event_date_ts: number
	event_subject: string
	event_from: string
	event_additional_data: object
}

export default function CampaignAnalytics(props: {
	campaignUID: string,
}) {
	const [pageData, setPageData] = useState<PageData>();
	const [positiveReplyCount, setPositiveReplyCount] = useState(0);
	const [negativeReplyCount, setNegativeReplyCount] = useState(0);
	const [neutralReplyCount, setNeutralReplyCount] = useState(0);

	// Query to fetch page data.
	const pageDataQuery = useQuery({
		queryKey: ["getCampaignAnalytics"],
		queryFn: () => authenticateAndFetchData(`/campaigns/details/analytics/?campaign_uid=${props.campaignUID}`),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	});
	useEffect(() => {
		if (pageDataQuery.data) {
			const data = pageDataQuery.data.data as PageData
			setPageData(data);
			setPositiveReplyCount(data.campaign_activities.filter(
				activity => activity.event_subject === "Positive Reply").length
			);
			setNeutralReplyCount(data.campaign_activities.filter(
				activity => activity.event_subject === "Neutral Reply").length
			);
			setNegativeReplyCount(data.campaign_activities.filter(
				activity => activity.event_subject === "Negative Reply").length
			);
		}
	}, [pageDataQuery.data]);

	const theme = useTheme();

	const subjectTextColor = (subject: string) => {
		switch (subject) {
			case "Positive Reply":
				return theme.palette.success.main;

			case "Negative Reply":
				return theme.palette.error.main;

			case "Neutral Reply":
				return theme.palette.primary.main;

			case "default":
				return theme.palette.error.main;
		}
	}

	const columns: GridColDef[] = [
		{
			field: 'activity_text',
			headerName: 'Event',
			editable: false,
			resizable: false,
			renderCell: (params) => {
				return (
					<Box display={"flex"} alignItems={"center"} sx={{height: "100%"}}>
						<Typography>
							Received&nbsp;
							<b>
							<span style={{color: subjectTextColor(params.row.event_subject)}}>
					  		{params.row.event_subject}</span>
							</b> from <b>{params.row.event_from}</b>&nbsp;
							on {format(new Date(params.row.event_date_ts), "do MMM y, HH:mm:ss (xxx)")}
						</Typography>
					</Box>
				)
			},
			flex: 1,
		},
		{
			field: 'action',
			headerName: 'Action',
			headerAlign: "right",
			align: "right",
			editable: false,
			resizable: false,
			renderCell: (params) => {
				if (params.row.event_type === "reply") {
					// Show reply button.
					return (
						<ViewReply replyFrom={params.row.event_from}
											 s3key={params.row.event_additional_data["email_s3_key"]}/>
					)
				} else {
					// Don't show anything.
					return null;
				}
			},
			flex: 1,
		},
	];

	const paginationModel = {page: 0, pageSize: 10};

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (pageDataQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (pageDataQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Stack spacing={4} direction={"column"} justifyContent={"center"} alignItems={"center"}>
				<Box sx={{display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gridGap: "10px", width: "100%"}}>
					{/* Total Contacts */}
					<Card sx={{borderRadius: 4, flexGrow: 1, minHeight: "120px"}}>
						<CardContent>
							<Typography variant={"body1"} align={"left"} color={"textSecondary"}>Total Contacts</Typography>
							<Typography variant={"h6"} align={"left"}>{pageData.total_contacts}</Typography>
						</CardContent>
					</Card>
					{/* Emails per Day */}
					<Card sx={{borderRadius: 4, flexGrow: 1, minHeight: "120px"}}>
						<CardContent>
							<Typography variant={"body1"} align={"left"} color={"textSecondary"}>Emails Per Day</Typography>
							<Typography variant={"h6"} align={"left"}>
								{pageData.emails_per_day > 0 ? pageData.emails_per_day : "Auto"}
							</Typography>
						</CardContent>
					</Card>
					{/* Total Emails Sent */}
					<Card sx={{borderRadius: 4, flexGrow: 1, minHeight: "120px"}}>
						<CardContent>
							<Typography variant={"body1"} align={"left"} color={"textSecondary"}>Total Emails Sent</Typography>
							<Typography variant={"h6"} align={"left"}>{pageData.total_emails_sent}</Typography>
						</CardContent>
					</Card>
					{/* Total Bounces */}
					<Card sx={{borderRadius: 4, flexGrow: 1, minHeight: "120px"}}>
						<CardContent>
							<Typography variant={"body1"} align={"left"} color={"textSecondary"}>Total Bounces</Typography>
							<Typography variant={"h6"} align={"left"}>
								{pageData.total_bounced}&nbsp;
							</Typography>
						</CardContent>
					</Card>
					{/* Total Replies */}
					<Card sx={{borderRadius: 4, flexGrow: 1, minHeight: "120px"}}>
						<CardContent>
							<Typography variant={"body1"} align={"left"} color={"textSecondary"}>Total Replies</Typography>
							<Typography variant={"h6"} align={"left"}>
								{pageData.total_replies}&nbsp;
							</Typography>
							<Typography variant={"caption"} sx={{fontSize: "0.7em"}} color={"textSecondary"}>
								Positive: {positiveReplyCount} | Neutral: {neutralReplyCount} | Negative: {negativeReplyCount}
							</Typography>
						</CardContent>
					</Card>
					{/* Total Unsubscribes */}
					<Card sx={{borderRadius: 4, flexGrow: 1, minHeight: "120px"}}>
						<CardContent>
							<Typography variant={"body1"} align={"left"} color={"textSecondary"}>Total Unsubscribed</Typography>
							<Typography variant={"h6"} align={"left"}>
								{pageData.total_unsubscribes}&nbsp;
							</Typography>
						</CardContent>
					</Card>
				</Box>

				{/* ---------------- Activity ---------------- */}
				<Box sx={{width: "100%", mt: 4}}>
					<Stack direction={"column"} sx={{width: "100%"}}>
						<Typography variant={"h6"}>Reply Activity</Typography>
						{/*<Divider orientation={"horizontal"} sx={{width: "100%", mt: 2}}/>*/}
						<DataGrid
							rows={pageData.campaign_activities}
							columns={columns}
							localeText={{
								noRowsLabel: "No Activity"
							}}
							pageSizeOptions={[10, 50, 100]}
							initialState={{pagination: {paginationModel}}}
							sx={{mb: 6, mt: 4}}
						/>
					</Stack>
				</Box>
			</Stack>
		)

	} else {
		// Ideally it should not reach here.
		return <></>
	}
}

const BootstrapDialog = styled(Dialog)(({theme}) => ({
	'& .MuiDialogContent-root': {
		padding: theme.spacing(2),
	},
	'& .MuiDialogActions-root': {
		padding: theme.spacing(1),
	},
}));

function ViewReply(props: {
	replyFrom: string,
	s3key: string,
}) {
	const [open, setOpen] = React.useState(false);
	const [replyMessage, setReplyMessage] = React.useState("");

	// Query to fetch reply email message.
	const replyMessageQuery = useQuery({
		queryKey: ["replyMessageQuery", props.replyFrom, props.s3key],
		queryFn: () => authenticateAndFetchData(`/campaigns/get-reply-email-message/?key=${props.s3key}`),
		gcTime: 5000,
		retry: retryFn,
		refetchOnWindowFocus: false,
	});
	useEffect(() => {
		if (replyMessageQuery.data) {
			setReplyMessage(replyMessageQuery.data.data["message"]);
		}
	}, [replyMessageQuery.data]);

	const handleClickOpen = () => {
		setOpen(true);
	};
	const handleClose = () => {
		setOpen(false);
	};

	if (replyMessageQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else {
		return (
			<React.Fragment>
				<Button onClick={handleClickOpen}>
					View Reply
				</Button>
				<BootstrapDialog
					onClose={handleClose}
					aria-labelledby="view-reply-dialog"
					open={open}
				>
					<DialogTitle sx={{m: 0, p: 2}} id="view-reply-dialog">
						Reply from <b>{props.replyFrom}</b>
					</DialogTitle>
					<DialogContent dividers>
						<Typography gutterBottom>
							<pre style={{whiteSpace: "pre-wrap", wordWrap: "break-word"}}>{replyMessage}</pre>
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button autoFocus onClick={handleClose}>
							Close
						</Button>
					</DialogActions>
				</BootstrapDialog>
			</React.Fragment>
		);
	}
}
